import { Chip, Stack } from '@mui/material';
import { DefaultMapping, Unit } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Localized } from '@synergy/components';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { padZeroLeft } from '@synergy/utils';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as UnitActions from '../../../../common/actions/unit-actions';
import { MappedValues } from '../../../../common/components/mapped-values';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface UnitsMappingOverviewProps {
    onEdit: (item: DefaultMapping<Unit>) => void;
}

const columns: PaloColumn<DefaultMapping<Unit>>[] = [
    {
        key: 'translation',
        type: 'name',
        head: <FormattedMessage id="app.table.column.name" />,
        cell: unit => (
            <>
                <Localized value={unit.translations} /> ({padZeroLeft(unit.id, 3)})
            </>
        ),
    },
    {
        key: 'mapping',
        type: 'name',
        head: <FormattedMessage id="app.table.column.mapping" />,
        cell: unit => <MappedValues values={unit.defaultMapping} />,
    },
];

export const UnitsMappingOverview = (props: UnitsMappingOverviewProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useThunkDispatch();
    const units = useAppSelector(state => state.units.defaultMappings);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    useEffect(() => {
        dispatch(UnitActions.fetchUnitsMapping(searchParams));
    }, [dispatch, searchParams]);

    return (
        <PaloListView<DefaultMapping<Unit>, number>
            embedded={true}
            columns={columns}
            items={units.items}
            getRowId={unit => unit.id}
            emptyLabel="Keine Einheiten."
            onRowClick={props.onEdit}
            rowActions={unit => [
                { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(unit) },
            ]}
            pagination={{
                count: units.total,
                page: Number(searchParams.get('page') ?? 1),
                limit: Number(searchParams.get('limit') ?? 50),
                onPageChange: page => updateParams({ page: String(page) }),
                onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
            }}
        />
    );
};
